import { useTranslate } from "@tolgee/react";
import { getAuth, OAuthProvider, signInWithPopup } from "firebase/auth";
import { useSetAtom } from "jotai";
import { useEffect, useState } from "react";

import { useGetSSOProvider, useLogin } from "@/api/__generated__/orval/auth";
import { app } from "@/config/firebase";
import { accessTokenAtom, refreshTokenAtom } from "@/store";

import { useToasts } from "../useToasts";

export const useSSOLogin = (domain = "") => {
  const [isLogging, setIsLogging] = useState(false);

  const { t } = useTranslate();
  const toaster = useToasts();

  const setAccessToken = useSetAtom(accessTokenAtom);
  const setRefreshToken = useSetAtom(refreshTokenAtom);

  const { mutate } = useLogin({
    mutation: {
      onSuccess: (data) => {
        setAccessToken(data.data.data?.accessToken || "");
        setRefreshToken(data.data.data?.refreshToken || "");
      },
    },
  });

  const {
    data,
    isLoading: isLogin,
    error: ssoProviderError,
  } = useGetSSOProvider({ domain }, { query: { enabled: !!domain } });

  useEffect(() => {
    const login = async () => {
      const providerName = data?.data?.data?.provider;

      if (!providerName) {
        return;
      }

      setIsLogging(true);

      const auth = getAuth(app);
      const provider = new OAuthProvider(providerName);
      provider.addScope("email");

      try {
        const { user } = await signInWithPopup(auth, provider);
        const token = await user.getIdToken();
        if (!token) {
          toaster.add({
            key: "fail-oauth",
            state: "error",
            title: t("signIn.ssoForm.errorSSO"),
          });
        }
        mutate({ data: { firebaseToken: token } });
      } catch (_e: unknown) {
        setIsLogging(false);
        toaster.add({
          key: "fail-oauth",
          state: "error",
          title: t("signIn.ssoForm.errorSSO"),
        });
        console.error(_e);
      }
    };

    if (isLogging) {
      return;
    }
    void login();
  }, [data?.data?.data?.provider, isLogging, mutate, t, toaster]);

  return {
    isLogin,
    ssoProviderError,
  };
};
